import React, {
  useCallback, useEffect, useRef, useState, useMemo,
} from 'react';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { TreeSelect as AntdTreeSelect, Badge } from 'antd';
import { TreeSelectProps as AntdTreeSelectProps } from 'antd/lib/tree-select';
import { RefSelectProps } from 'antd/lib/select';
import type { DataNode } from '@rc-component/tree-select/lib/interface';
import { CaretDownFilled } from '@ant-design/icons';
import { SelectPropsCommon, SelectOptionType } from './types';
import cn from './styles.module.scss';

export interface MultiSelectProps<
  ValueType = any[],
  OptionType extends SelectOptionType = SelectOptionType
> extends SelectPropsCommon,
  Omit<AntdTreeSelectProps<ValueType, OptionType>, 'treeData' | 'onChange' | 'onSelect'> {
  options?: OptionType[],
  onChange?: (value: ValueType) => void,
  showSelectAllOption?: boolean,
  multiple: true,
}

const isLeaf = (item: SelectOptionType) => !item.options?.length;

function collectLeafValues(items: SelectOptionType[]): SelectOptionType['value'][] {
  return items.flatMap((node) => {
    if (node.disabled) return [];
    if (isLeaf(node)) return [node.value];
    return collectLeafValues(node.options!);
  });
}

function decodeSelectedLeaves(
  items: SelectOptionType[],
  value: readonly SelectOptionType['value'][],
): Set<SelectOptionType['value']> {
  const valueSet = new Set(value);
  const collect = (nodes: SelectOptionType[], inherit: boolean): SelectOptionType['value'][] =>
    nodes.flatMap((node) => {
      if (node.disabled) return [];
      const selected = inherit || valueSet.has(node.value);
      if (isLeaf(node)) return selected ? [node.value] : [];
      return collect(node.options!, selected);
    });
  return new Set(collect(items, false));
}

function encodeStrategy(
  items: SelectOptionType[],
  selectedLeaves: Set<SelectOptionType['value']>,
  strategy: AntdTreeSelectProps['showCheckedStrategy'],
): SelectOptionType['value'][] {
  const isFullyChecked = (node: SelectOptionType): boolean => {
    if (node.disabled) return false;
    if (isLeaf(node)) return selectedLeaves.has(node.value);
    return node.options!.every((c) => c.disabled || isFullyChecked(c));
  };

  return items.flatMap(function step(node): SelectOptionType['value'][] {
    if (isLeaf(node)) {
      return !node.disabled && selectedLeaves.has(node.value) ? [node.value] : [];
    }
    const fullyChecked = !node.disabled && isFullyChecked(node);
    if (strategy === AntdTreeSelect.SHOW_PARENT) {
      return fullyChecked ? [node.value] : node.options!.flatMap(step);
    }
    if (strategy === AntdTreeSelect.SHOW_ALL) {
      const children = node.options!.flatMap(step);
      return fullyChecked ? [node.value, ...children] : children;
    }
    return node.options!.flatMap(step); // SHOW_CHILD
  });
}

function filterOptionsTree(
  items: SelectOptionType[],
  search: string,
  treeNodeFilterProp: string,
  filterTreeNode?: AntdTreeSelectProps['filterTreeNode'],
): SelectOptionType[] {
  if (!search || filterTreeNode === false) return items;

  const matchFn: (search: string, node: SelectOptionType) => boolean = typeof filterTreeNode === 'function'
    ? (filterTreeNode as unknown as (search: string, node: SelectOptionType) => boolean)
    : (_, node) =>
      String((node as Record<string, unknown>)[treeNodeFilterProp] ?? '')
        .toUpperCase()
        .includes(search.toUpperCase());

  const recurse = (nodes: SelectOptionType[], keepAll: boolean): SelectOptionType[] =>
    nodes.reduce<SelectOptionType[]>((acc, node) => {
      const matched = keepAll || matchFn(search, node);
      const filteredChildren = recurse(node.options ?? [], matched);
      if (matched || filteredChildren.length) {
        acc.push({ ...node, options: filteredChildren });
      }
      return acc;
    }, []);

  return recurse(items, false);
}

export function MultiSelect<
  ValueType = any[],
  OptionType extends SelectOptionType = SelectOptionType,
>(props: MultiSelectProps<ValueType, OptionType>) {
  const {
    options = [],
    value,
    placeholder,
    onChange,
    size,
    debounceTime,
    popupClassName,
    showSelectAllOption = false,
    showCheckedStrategy = AntdTreeSelect.SHOW_CHILD,
    popupRender,
    showSearch: showSearchProp,
    ...restProps
  } = props;

  const [selectValue, setSelectValue] = useState<ValueType | undefined>(value);
  const [searchValue, setSearchValue] = useState('');
  const ref = useRef<RefSelectProps>(null);

  useEffect(() => {
    setSelectValue(value);
  }, [value]);

  const handleSelectChange = useCallback(
    debounceTime
      ? debounce((newValue) => onChange && onChange(newValue), debounceTime)
      : onChange as any,
    [onChange],
  );

  const consumerSearchConfig = typeof showSearchProp === 'object' ? showSearchProp : undefined;
  const treeNodeFilterProp = consumerSearchConfig?.treeNodeFilterProp ?? 'label';
  const filterTreeNode = consumerSearchConfig?.filterTreeNode;

  const filteredOptions = useMemo(
    () => filterOptionsTree(options, searchValue, treeNodeFilterProp, filterTreeNode),
    [options, searchValue, treeNodeFilterProp, filterTreeNode],
  );

  const filteredLeafValues = useMemo(() => collectLeafValues(filteredOptions), [filteredOptions]);

  const selectedLeaves = useMemo(
    () => decodeSelectedLeaves(options, (selectValue as SelectOptionType['value'][] | undefined) ?? []),
    [options, selectValue],
  );

  const isAllFilteredSelected = filteredLeafValues.length > 0
    && filteredLeafValues.every((v) => selectedLeaves.has(v));
  const isSomeFilteredSelected = filteredLeafValues.some((v) => selectedLeaves.has(v));

  const handleChange = (newValue: ValueType) => {
    setSelectValue(newValue);
    handleSelectChange(newValue);
  };

  const handleSelectAll = useCallback(() => {
    if (filteredLeafValues.length === 0) return;
    const next = new Set(selectedLeaves);
    if (isAllFilteredSelected) {
      filteredLeafValues.forEach((v) => next.delete(v));
    } else {
      filteredLeafValues.forEach((v) => next.add(v));
    }
    const newValue = encodeStrategy(options, next, showCheckedStrategy);
    setSelectValue(newValue as ValueType);
    handleSelectChange(newValue);
  }, [
    filteredLeafValues,
    isAllFilteredSelected,
    selectedLeaves,
    options,
    showCheckedStrategy,
    handleSelectChange,
  ]);

  // Mirrors antd OptionList: blocks text selection on click/drag and any
  // edge-case focus shift off the search input. Click still flows through.
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const selectAllPopupRender = useCallback((menu: React.ReactElement) => {
    const renderedMenu = popupRender ? popupRender(menu) : menu;
    const isFiltering = !!searchValue;
    const isDisabled = filteredLeafValues.length === 0;
    const label = isAllFilteredSelected
      ? (isFiltering ? 'Unselect All Filtered' : 'Unselect All')
      : (isFiltering ? 'Select All Filtered' : 'Select All');
    const isIndeterminate = !isAllFilteredSelected && isSomeFilteredSelected;
    return (
      <>
        <div
          className={classNames(cn.selectAll, { [cn.selectAllDisabled]: isDisabled })}
          onMouseDown={handleMouseDown}
          onClick={isDisabled ? undefined : handleSelectAll}
          role="button"
          aria-disabled={isDisabled}
        >
          <div className={cn.selectAllBody}>
            <span
              className={classNames(cn.selectAllBox, {
                [cn.selectAllBoxChecked]: isAllFilteredSelected,
                [cn.selectAllBoxIndeterminate]: isIndeterminate,
                [cn.selectAllBoxDisabled]: isDisabled,
              })}
              role="checkbox"
              aria-checked={isIndeterminate ? 'mixed' : isAllFilteredSelected}
              aria-disabled={isDisabled}
            />
            <span className={cn.selectAllLabel} title={label}>{label}</span>
          </div>
        </div>

        {renderedMenu}
      </>
    );
  }, [
    popupRender,
    searchValue,
    isAllFilteredSelected,
    isSomeFilteredSelected,
    filteredLeafValues.length,
    handleSelectAll,
    handleMouseDown,
  ]);

  return (
    <AntdTreeSelect
      ref={ref}
      placeholder={placeholder}
      popupMatchSelectWidth={false}
      maxTagCount={0}
      maxTagPlaceholder={() => (
        <div className={classNames(cn.tag, { [cn.hideTag]: searchValue })}>
          <span>{placeholder}</span>
        </div>
      )}
      treeCheckable
      suffixIcon={
        <div className={cn.suffix}>
          <Badge size={size === 'large' ? 'medium' : size} color="var(--bc-color-primary)" count={(selectValue as ValueType[])?.length || 0} overflowCount={999} />
          <CaretDownFilled />
        </div>
      }
      showCheckedStrategy={showCheckedStrategy}
      value={selectValue || undefined}
      treeData={options}
      popupRender={showSelectAllOption ? selectAllPopupRender : popupRender}
      showSearch={showSearchProp === false ? false : {
        ...consumerSearchConfig,
        searchValue,
        onSearch: setSearchValue,
        treeNodeFilterProp,
        filterTreeNode,
      }}
      fieldNames={{
        label: 'label',
        children: 'options',
      }}
      size={size}
      onChange={handleChange}
      {...restProps as Omit<AntdTreeSelectProps<ValueType, DataNode>, 'treeData' | 'onChange' | 'onSelect'>}
      classNames={{
        ...restProps.classNames,
        popup: {
          ...restProps.classNames?.popup,
          root: classNames(cn.treeDropdown, popupClassName),
          item: classNames(cn.treeItem, restProps.classNames?.popup?.item)
        }
      }}
    />
  );
}
